import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter, firstValueFrom, timeout } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GameEventService {
  private gameEventSource: Subject<{ name: string; data: any }> = new Subject<{
    name: string;
    data: any;
  }>();
  public gameEvent$: Observable<{ name: string; data: any }>;
  /**
   * true while restore events are emitted: they rebuild what is on the table, so views
   * skip sounds, speech bubbles and info dialogs for them (that already happened)
   */
  public replaying = false;
  /** game views (desktop / mobile) currently listening to gameEvent$ */
  private views = new BehaviorSubject(0);

  constructor() {
    this.gameEvent$ = this.gameEventSource.asObservable();
  }

  public emit(v: { name: string; data: any }): void {
    this.gameEventSource.next(v);
  }

  /** emits past events to rebuild the table, without their sounds and bubbles */
  public replay(events: { name: string; data: any }[]): void {
    this.replaying = true;
    try {
      for (const event of events) this.emit(event);
    } finally {
      this.replaying = false;
    }
  }

  /** a game view subscribed to gameEvent$ (call in ngOnInit, after subscribing) */
  public viewAttached(): void {
    this.views.next(this.views.value + 1);
  }

  public viewDetached(): void {
    this.views.next(Math.max(0, this.views.value - 1));
  }

  /**
   * Resolves once a game view listens (plus one task, so its child components are set up
   * too). Events emitted before that are lost for the view: e.g. the "Was willst du
   * ansagen?" picker opens only from the view's waitingFor handler. Gives up after
   * `maxMs` rather than hanging.
   */
  public async whenViewAttached(maxMs = 5000): Promise<void> {
    try {
      await firstValueFrom(this.views.pipe(filter(count => count > 0), timeout(maxMs)));
    } catch {
      // no view within maxMs: go on anyway
    }
    await new Promise(resolve => setTimeout(resolve));
  }
}
